import { useState } from 'react';
import {
  RESEARCH_DEPTHS,
  RESEARCH_DEPTH_DEFAULTS_BY_VERTICAL,
  SUBJECT_VOCABULARY_BY_VERTICAL,
} from '@ni/shared';
import { api, errorLine } from '../../api/client';
import type { AdminOrg } from '../../api/types';
import { ChoiceField } from '../../components/ui/choice-field';
import { budgetUnusable } from '../../lib/budget';
import {
  RESEARCH_DEPTH_HELP,
  RESEARCH_DEPTH_LABEL,
  RESEARCH_DEPTH_LABELS,
  RESEARCH_SECTION_NOTE,
  statusLabel,
} from '../../lib/copy';
import {
  researchBagOf,
  researchDraftOf,
  researchSaveWithheld,
  type ResearchDraft,
} from '../../lib/research';

/**
 * **The organization's research settings** — how much research each status is
 * worth, and the spend limit per run. Both are the operator's (2026-09-04,
 * Soroush): the engine checks a cap **between rounds**, so a run overshoots by
 * the wave already in flight plus its final write-up — an operator may set a
 * ceiling knowing that, and an office would reasonably read one as absolute.
 * The reading model and effort are platform constants, not settings
 * (docs/notes/2026-09-26-run-defaults-sweep.md Q4).
 *
 * The depth controls are **one per status in the organization's own
 * vocabulary**, read from `@ni/shared` — the same table the server resolves a
 * run out of. A build that does not carry this organization's vertical renders
 * none of them rather than guessing at a vocabulary; the budget is core and is
 * shown either way.
 *
 * The whole bag is sent on save, because naming it **replaces** what is stored:
 * every control is on screen together, so a key left out is a choice taken back.
 * `saved` is the value the server last confirmed, so the button is offered only
 * when the form differs from it — and after a save that is what "unchanged"
 * means, without a re-read of the list.
 */
export function ResearchSettingsForm({
  org,
  onSaved,
}: {
  org: AdminOrg;
  onSaved?: (org: AdminOrg) => void;
}) {
  const [saved, setSaved] = useState(() => researchDraftOf(org.research));
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statuses = SUBJECT_VOCABULARY_BY_VERTICAL[org.vertical]?.statuses ?? [];
  const depthDefaults = RESEARCH_DEPTH_DEFAULTS_BY_VERTICAL[org.vertical];
  const unusableBudget = budgetUnusable(draft.budget);
  const withheld = researchSaveWithheld({ draft, saved, vertical: org.vertical, saving });

  const edit = (change: Partial<ResearchDraft>) => setDraft({ ...draft, ...change });

  const save = async () => {
    if (withheld) return;
    setSaving(true);
    try {
      const answer = await api.updateOrgSettings(org.id, researchBagOf(draft, org.vertical));
      const settled = researchDraftOf(answer.org.research);
      setSaved(settled);
      setDraft(settled);
      setError(null);
      onSaved?.(answer.org);
    } catch (caught) {
      setError(errorLine(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form">
      <div className="field">
        <div className="field-note">{RESEARCH_SECTION_NOTE}</div>
      </div>

      {statuses.length > 0 ? (
        <div className="field">
          <span className="label">{RESEARCH_DEPTH_LABEL}</span>
          <div className="field-note">{RESEARCH_DEPTH_HELP}</div>
          {statuses.map((status) => {
            const fallback = depthDefaults?.[status];
            return (
              <ChoiceField
                key={status}
                id={`org-depth-${status}`}
                label={statusLabel(status)}
                value={draft.depthByStatus[status] ?? ''}
                options={RESEARCH_DEPTHS}
                labels={RESEARCH_DEPTH_LABELS}
                defaultLabel={fallback ? RESEARCH_DEPTH_LABELS[fallback] : null}
                disabled={saving}
                onChange={(depth) =>
                  edit({ depthByStatus: { ...draft.depthByStatus, [status]: depth } })
                }
              />
            );
          })}
        </div>
      ) : null}

      <div className={unusableBudget ? 'field over' : 'field'}>
        <label className="label" htmlFor="org-budget">
          Spend limit per run
        </label>
        <input
          id="org-budget"
          type="text"
          inputMode="decimal"
          value={draft.budget}
          disabled={saving}
          aria-invalid={unusableBudget || undefined}
          aria-describedby={unusableBudget ? 'org-budget-note' : undefined}
          onChange={(event) => edit({ budget: event.target.value })}
        />
        {draft.budget.trim().length === 0 ? <div className="field-note">No limit.</div> : null}
        {unusableBudget ? (
          <div id="org-budget-note" className="field-note over-note">
            Write an amount in dollars greater than zero, or leave it blank for no limit.
          </div>
        ) : null}
        <div className="field-note">
          The most one research run may spend. The limit is checked between rounds, so a run can
          overshoot by the wave of work already under way plus its final write-up.
        </div>
      </div>

      {error ? (
        <div className="flag row">
          <div className="label">Error</div>
          <div className="detail">{error}</div>
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" className="btn go" disabled={withheld} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
